import type { TranslationKeys } from "./en"

export const de: TranslationKeys = {
  // App
  appName: "DevPulse",

  // Navigation
  navDashboard: "Dashboard",
  navContributors: "Mitwirkende",
  navTeams: "Teams",
  navRepositories: "Repositories",

  // Views
  viewOrganization: "Organisation",
  viewGroup: "Gruppe",
  viewIndividual: "Individuell",
  viewSelectPlaceholder: "Ansicht wählen",

  // Common
  loading: "Daten werden geladen...",
  authenticating: "Authentifizierung läuft...",
  loadingError: "Fehler beim Laden der Daten",
  authError: "Authentifizierungsfehler",
  retry: "Erneut versuchen",
  tryAgain: "Nochmal versuchen",
  profile: "Profil",
  settings: "Einstellungen",
  logout: "Abmelden",
  selectTimeRange: "Zeitraum wählen",
  selectTeam: "Team auswählen...",
  selectDeveloper: "Entwickler auswählen...",
  normalizedView: "Normalisiert",
  absoluteView: "Absolut",

  // Time ranges
  timeLast7Days: "Letzte 7 Tage",
  timeLast14Days: "Letzte 2 Wochen",
  timeLast28Days: "Letzte 4 Wochen",
  timeLast8Weeks: "Letzte 8 Wochen",
  timeLast12Weeks: "Letzte 12 Wochen",
  timeLast30Days: "Letzte 30 Tage",
  timeAll: "Alle",

  // Dashboard titles
  dashboardOrganizationTitle: "Organisations-Dashboard",
  dashboardOrganizationSubtitle:
    "Übersicht der Entwicklungsmetriken über alle Repositories und Teammitglieder hinweg",
  dashboardTeamTitle: "Team-Dashboard",
  dashboardTeamSubtitle: "Entwicklungsmetriken für {team} Teammitglieder",
  dashboardTeamSubtitleGeneric: "Entwicklungsmetriken für Teammitglieder",
  dashboardTeamNoData:
    "Keine Teams konfiguriert. Bitte fügen Sie Teamdaten hinzu, um teamspezifische Metriken anzuzeigen.",
  dashboardIndividualTitle: "Individuelles Dashboard",
  dashboardIndividualSubtitle: "Entwicklungsmetriken für {author}",
  dashboardIndividualSubtitleGeneric: "Entwicklungsmetriken für einzelne Mitwirkende",
  dashboardIndividualNoData:
    "Keine Autoren gefunden. Bitte laden Sie Daten, um individuelle Metriken anzuzeigen.",

  // Contributors page
  contributorsTitle: "Mitwirkende",
  contributorsSubtitle: "Leistung und Aktivität der Mitwirkenden analysieren",
  contributorsNoData: "Keine Mitwirkendendaten für den ausgewählten Zeitraum verfügbar.",

  // Teams page
  teamsTitle: "Teams",
  teamsSubtitle: "Teamleistung und -aktivität analysieren",
  teamsNoData: "Keine Teamdaten für den ausgewählten Zeitraum verfügbar.",

  // Repositories page
  repositoriesTitle: "Repositories",
  repositoriesSubtitle: "Repository-Leistung und -Aktivität analysieren",
  repositoriesNoData: "Keine Repository-Daten für den ausgewählten Zeitraum verfügbar.",
  repositoriesScoreTitle: "Repository-Commit-Komplexitätsscore",
  repositoriesScoreDescription: "Gesamtkomplexitätsscore aller Commits pro Repository.",
  repositoriesPRsTitle: "Repository Pull Requests",
  repositoriesPRsDescription: "Anzahl der zusammengeführten Pull Requests pro Repository.",

  // DORA Metrics page
  doraMetricsTitle: "DORA-Metriken",
  doraMetricsSubtitle:
    "DevOps Research and Assessment (DORA) Metriken zur Messung der Software-Delivery-Performance",
  doraMetricsSubtitleTeam: "DORA-Metriken für {team} Teammitglieder",
  doraMetricsSubtitleIndividual: "DORA-Metriken für {author}",
  doraNoDataTitle: "Keine DORA-Metrikdaten verfügbar",
  doraNoDataDescription:
    "DORA-Metriken erfordern Releases, Pull Requests und Commit-Daten. Bitte stellen Sie sicher, dass Daten gesammelt und exportiert werden.",
  doraMissingDataTitle: "Fehlende Daten für vollständige DORA-Metriken",
  doraMissingDataDescription:
    "Die folgenden Datenpunkte fehlen oder sind unvollständig für die vollständige DORA-Metrikberechnung:",
  doraDataLimitationsTitle: "Datenbeschränkungen",
  doraDataLimitationsDescription:
    "Die angezeigten DORA-Metriken werden aus verfügbaren Daten berechnet, die einige Einschränkungen haben:",
  doraOverallPerformance: "Gesamtes DORA-Leistungsniveau",
  doraOverallDescription:
    "Basierend auf den vier Schlüsselmetriken wird das Gesamtleistungsniveau Ihres Teams gemäß DORA-Benchmarks berechnet.",
  doraDeploymentFrequency: "Deployment-Frequenz",
  doraLeadTime: "Durchlaufzeit für Änderungen",
  doraChangeFailureRate: "Änderungs-Fehlerrate",
  doraTimeToRestore: "Zeit zur Wiederherstellung des Dienstes",
  doraPerMonth: "pro Monat",
  doraPerWeek: "pro Woche",
  doraDays: "Tage",
  doraHours: "Stunden",
  doraDeploymentsByMonth: "Deployments pro Monat",
  doraDeploymentsByWeek: "Deployments pro Woche",
  doraLeadTimeTrend: "Durchlaufzeit-Trend",
  doraFailureRateTrend: "Fehlerrate-Trend",
  doraRestoreTimeTrend: "Wiederherstellungszeit-Trend",
  doraDeploymentFrequencyDetail: "Deployment-Frequenz über Zeit",
  doraDeploymentFrequencyDetailDescription: "Anzahl der Deployments pro Periode",
  doraLeadTimeDetail: "Durchlaufzeit für Änderungen über Zeit",
  doraLeadTimeDetailDescription:
    "Durchschnittliche Zeit von PR-Erstellung bis Merge (in Stunden) pro Periode",
  doraPeriodWeekly: "Wöchentlich",
  doraPeriodMonthly: "Monatlich",
  doraPeriodSelectLabel: "Periode",
  doraMethodologyTitle: "Methodik & Datenquellen",
  doraDeploymentFrequencyMethodology:
    "Berechnet aus veröffentlichten Releases (ohne Entwürfe und Pre-Releases) in der Releases-Datenbanktabelle.",
  doraLeadTimeMethodology:
    "Berechnet als Zeit zwischen Pull-Request-Erstellung und Merge, gemessen aus der pull_requests-Tabelle.",
  doraChangeFailureRateMethodology:
    "Geschätzt durch Erkennung von Bugfix- oder Revert-Commits innerhalb von 7 Tagen nach einem Release. Dies ist eine Annäherung, da wir keine explizite Deployment-Fehler-Verfolgung haben.",
  doraTimeToRestoreMethodology:
    "Geschätzt als Zeit zwischen einem Release und dem ersten Bugfix/Revert-Commit. Dies ist eine Annäherung, da wir keine Incident-Management-Daten haben.",

  // DORA Performance Levels
  doraLevelElite: "ELITE",
  doraLevelHigh: "HOCH",
  doraLevelMedium: "MITTEL",
  doraLevelLow: "NIEDRIG",

  // DORA Missing Data Points
  doraMissingReleases: "Release-Daten für Deployment-Häufigkeit",
  doraMissingPullRequests: "Pull-Request-Daten für Lead-Time-Berechnung",
  doraMissingCommits: "Commit-Daten für Fehleranalyse",
  doraMissingDeployments: "Deployment-Daten (Phase 2) - verwenden Releases als Proxy",
  doraMissingWorkflowRuns: "CI/CD-Workflow-Daten (Phase 2) - eingeschränkte Pipeline-Sichtbarkeit",
  doraMissingIncidents: "Incident-Tracking-Daten (Phase 3) - verwenden Bugfix-Commits als Proxy",

  // DORA Partial Metrics Notice
  doraPartialMetricsTitle: "Partielle DORA-Metriken",
  doraPartialMetricsDescription:
    "Diese Metriken werden anhand verfügbarer Daten (Releases, Pull Requests und Commits) berechnet. Für genauere Metriken werden GitHub Actions Workflow-Runs und Incident-Tracking-Daten benötigt.",
  doraEstimatedBadge: "Geschätzt",
  doraAccurateBadge: "Genau",
  doraDataQualityEstimated: "Verwendet Proxy-Daten für die Berechnung",
  doraDataQualityAccurate: "Verwendet tatsächliche Deployment- und Incident-Daten",
  doraNoDataAvailable: "Keine Daten für diese Metrik verfügbar",

  error: "Fehler",

  // Tabs
  tabPerformance: "Leistung",
  tabScore: "Bewertung",
  tabPRs: "PRs",
  tabIssues: "Issues",
  tabActivity: "Aktivität",
  tabReleases: "Releases",
  tabStaffing: "Besetzung",

  // Staffing tab
  staffingTitle: "Team-Besetzungsübersicht",
  staffingDescription: "Anzahl der Teammitglieder pro Team, sortiert nach Teamgröße.",
  staffingMemberCount: "{count} Mitglieder",
  staffingMember: "{count} Mitglied",

  // Activity
  recentActivity: "Letzte Aktivität",

  // Releases tab
  releasesTitle: "Releases (Letzte 12 Wochen)",
  releasesDescription: "Gesamtzahl der veröffentlichten Releases in allen Repositories",
  releasesChartTitle: "Releases nach Repository (Letzte 6 Wochen)",
  releasesChartDescription: "Wöchentliche Release-Aktivität pro Repository",

  // Performance tab
  performanceScoreTitle: "Gesamt-Leistungsbewertung (0-100)",
  performanceScoreDescription:
    "Eine umfassende Metrik, die Codequalität (30%), Liefergeschwindigkeit (25%), Code-Impact (20%), Problemlösung (15%) und Arbeitsvielfalt (10%) kombiniert.",
  performanceScoreAverage: "Durchschnitt: {value}",
  performanceScoreComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  performanceScoreTeamTitle: "Gesamt-Team-Leistungsbewertung (0-100)",
  performanceScoreTeamDescription:
    "Aggregierte Leistungsbewertung, die alle Beiträge der Teammitglieder über Codequalität, Liefergeschwindigkeit, Code-Impact, Problemlösung und Arbeitsvielfalt hinweg kombiniert.",

  // Score tab
  scoreTitle: "Commit-Komplexitätsbewertung",
  scoreDescription:
    "Misst die Code-Komplexität basierend auf geänderten Dateien, modifizierten Zeilen, Funktionsänderungen und zyklomatischer Komplexität. Höhere Bewertungen weisen auf anspruchsvollere und wirkungsvollere Beiträge hin.",
  scoreAverage: "Durchschnitt: {value} Punkte",
  scoreComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  scoreTeamTitle: "Team-Commit-Komplexitätsbewertung",
  scoreTeamDescription:
    "Kombinierte Komplexitätsbewertung aus allen Commits der Teammitglieder, die die technische Herausforderung und den Impact von Code-Änderungen misst. Höhere Bewertungen weisen auf komplexere und wirkungsvollere Arbeit hin.",

  // PRs tab
  prsTitle: "Zusammengeführte Pull Requests",
  prsDescription:
    "Gesamtzahl der erfolgreich zusammengeführten Pull Requests. Diese Metrik spiegelt die Teilnahme am Code-Review, Zusammenarbeit und erfolgreiche Bereitstellung von Features und Fixes wider.",
  prsAverage: "Durchschnitt: {value} PRs",
  prsComparison:
    " — Der Prozentsatz zeigt, wie jeder Mitwirkende im Vergleich zu diesem Durchschnitt abschneidet.",

  prsTeamTitle: "Team zusammengeführte Pull Requests",
  prsTeamDescription:
    "Gesamte zusammengeführte Pull Requests von allen Teammitgliedern. Dies spiegelt Teamzusammenarbeit, Teilnahme am Code-Review und erfolgreiche Bereitstellung von Features und Fixes wider.",

  // Performance tab - Insight cards
  insightMergedPRs: "Zusammengeführte PRs",
  insightPRThroughput: "PR-Durchsatz",
  insightFeatureCommits: "Feature-Commits",
  insightFeatures: "Features",
  insightBugfixCommits: "Bugfix-Commits",
  insightBugfixes: "Bugfixes",
  insightMaintenanceCommits: "Wartungs-Commits",
  insightMaintenance: "Wartung",

  // Chart titles
  chartCommitsByClassification: "Commits nach Klassifizierung",
  chartCommitsBySize: "Commits nach Größe",
  chartRepositoryCommits: "Commits nach Repository",
  chartRepositoryCommitsDesc: "Anzahl der Commits zu jedem Repository in {timeRange}",
  chartRepositoryPRsAndCommits: "PRs und Commits nach Repository",
  chartRepositoryPRsAndCommitsDesc:
    "Anzahl der Pull Requests und Commits zu jedem Repository in {timeRange}",
  chartCommitScoresByWeek: "Commit-Bewertungen nach Woche",
  chartCommitScoresByWeekDesc:
    "Gesamte Commit-Komplexitätsbewertungen gruppiert nach Woche und gestapelt nach Größe",
  chartCommitScoresByClassificationWeek: "Commit-Bewertungen nach Klassifizierung",
  chartCommitScoresByClassificationWeekDesc:
    "Gesamte Commit-Komplexitätsbewertungen gruppiert nach Woche und gestapelt nach Klassifizierung",
  chartPRsMerged: "Diesen Monat zusammengeführte PRs",
  chartPRsMergedByWeek: "PRs nach Woche zusammengeführt",
  chartPRsMergedByWeekDesc:
    "Anzahl der zusammengeführten Pull Requests pro Woche für die letzten 6 Wochen",
  chartCodeChanges: "Code-Änderungen",
  chartCodeChangesByWeek: "Code-Änderungen nach Woche",
  chartCodeChangesByWeekDesc:
    "Hinzugefügte und entfernte Zeilen aus zusammengeführten PRs pro Woche",
  chartIssuesCreated: "Diesen Monat erstellte Issues",
  chartIssuesClosed: "Diesen Monat geschlossene Issues",
  chartActivityTitle: "Beitragsaktivität",
  chartActivityDescription:
    "Organisationsweite Aktivität einschließlich Commits, Pull Requests und Issues im letzten Jahr",
  chartDetails: "Details",

  // Chart labels
  linesAdded: "Zeilen hinzugefügt",
  linesRemoved: "Zeilen entfernt",

  // Language selector
  languageLabel: "Sprache",
  languageEnglish: "Englisch",
  languageGerman: "Deutsch",
  languageSpanish: "Spanisch",
  languageChinese: "Chinesisch",

  // DataTable
  dataTableFilters: "Filter",
  dataTableFilterBy: "Filtern nach:",
  dataTableNoResults: "Keine Ergebnisse.",
  dataTableRowsPerPage: "Zeilen pro Seite:",
  dataTablePage: "Seite",
  dataTableOf: "von",
  dataTableTotalRows: "Zeilen gesamt",
  dataTableSearch: "Suchen...",

  // Charts
  chartTrend: "Trend",
  chartDistribution: "Verteilung",
  chartDate: "Datum",

  // Quick insights
  quickInsightDate: "Letzte 7 Tage",
  quickInsightVsLastWeek: "vs {count} letzte Woche",
  quickInsightVsLast7Days: "vs {count} vorherige 7 Tage",
  quickInsightVsLast14Days: "vs {count} vorherige 14 Tage",
  quickInsightVsLast28Days: "vs {count} vorherige 28 Tage",
  quickInsightVsLast8Weeks: "vs {count} vorherige 8 Wochen",
  quickInsightVsLast12Weeks: "vs {count} vorherige 12 Wochen",
  quickInsightVsLastMonth: "vs {count} letzter Monat",

  // Data table column headers
  tableColumnTitle: "Titel",
  tableColumnMessage: "Nachricht",
  tableColumnAuthor: "Autor",
  tableColumnDate: "Datum",
  tableColumnWeekday: "Wochentag",
  tableColumnCreated: "Erstellt",
  tableColumnScore: "Bewertung",
  tableColumnFiles: "Dateien",
  tableColumnAdditions: "+",
  tableColumnDeletions: "-",
  tableColumnType: "Typ",
  tableColumnTeam: "Team",
  tableColumnContributor: "Beitragender",
  tableColumnValue: "Wert",
  tableColumnCount: "Anzahl",

  // Chart descriptions
  chartCommitsByClassificationDesc:
    "Anzahl der Commits gruppiert nach Klassifizierung (Commit-Typ) und gestapelt nach Komplexitätsgröße",
  chartCommitsBySizeDesc:
    "Anzahl der Commits gruppiert nach Komplexitätsgröße und gestapelt nach Klassifizierung (Commit-Typ)",
  chartCodeChangesDesc: "Hinzugefügte und entfernte Zeilen aus zusammengeführten PRs",
  chartPRsMergedDesc: "{count} PRs zusammengeführt (vs {prevCount} vorheriger Zeitraum)",
  chartIssuesCreatedDesc: "{count} Issues erstellt (vs {prevCount} vorheriger Zeitraum)",
  chartIssuesClosedDesc: "{count} Issues geschlossen (vs {prevCount} vorheriger Zeitraum)",

  // Top performers tables
  topTeamsTitle: "Top Teams",
  topContributorsTitle: "Top Contributors",
  topContributorsTeamMembersLabel: "Teammitglieder",
  topContributorsExternalLabel: "Externe Contributors",
  topTeamRepositoriesTitle: "Top Repositories",
  topTeamRepositoriesDescription: "Repositories, zu denen das Team am meisten beiträgt",

  // Common labels
  commits: "Commits",
  pullRequests: "Pull Requests",

  // Settings page
  settingsTitle: "Einstellungen",
  settingsSubtitle: "Anwendungseinstellungen und Präferenzen verwalten",
  settingsClassificationTitle: "Klassifizierung",
  settingsClassificationDescription:
    "Wählen Sie, welche Commit-Klassifizierungen in Diagrammen und Metriken enthalten sein sollen. Änderungen gelten für alle Dashboard-Ansichten.",
  settingsTeamsTitle: "Teams",
  settingsTeamsDescription:
    "Wählen Sie, welche Teams in der Anwendung angezeigt werden sollen. Nicht ausgewählte Teams werden in keiner Ansicht angezeigt.",
  settingsSelectAll: "Alle auswählen",
  settingsDeselectAll: "Alle abwählen",
  settingsNoTeams:
    "Keine Teams verfügbar. Teams werden hier angezeigt, sobald Daten geladen wurden.",

  // Classification types
  classificationFeature: "Feature",
  classificationBugfix: "Bugfix",
  classificationRefactoring: "Refactoring",
  classificationCi: "CI/CD",
  classificationTests: "Tests",
  classificationDocs: "Dokumentation",
  classificationChore: "Wartung",
  classificationPerf: "Performance",
  classificationRevert: "Rückgängig",
  classificationMerge: "Merge",
  classificationArchive: "Archiv",
  classificationStyle: "Stil",
  classificationOther: "Andere",
  classificationUnclassified: "Nicht klassifiziert",

  // Search
  searchTeams: "Teams suchen...",
  searchContributors: "Mitwirkende suchen...",
  searchRepositories: "Repositories suchen...",
  searchCommits: "Commits durchsuchen...",
  noSearchResults: "Keine Ergebnisse für Ihre Suche gefunden.",

  // Commits table
  commitsTableTitle: "Alle Commits",
  commitsTableDescription: "Detaillierte Ansicht aller Commits im ausgewählten Zeitraum",
  tableColumnRepo: "Repo",
  tableColumnClassification: "Klassifizierung",

  // Performance explanation
  performanceExplanationTitle: "Erklärung der Leistungsbewertung",
  performanceExplanationSubtitle:
    "Verstehen, wie die Leistungsbewertung berechnet wird und was sie bedeutet",
  performanceMethodologyTitle: "Berechnungsmethodik",
  performanceMethodologyDescription:
    "Die Leistungsbewertung ist eine umfassende Metrik (0-100), die mehrere Dimensionen des Entwicklerbeitrags kombiniert:",
  performanceQualityWeight: "Code-Qualität (30%)",
  performanceQualityDesc:
    "Basierend auf Commit-Komplexitätswerten. Höhere Komplexitätswerte zeigen anspruchsvollere und wirkungsvollere Beiträge an.",
  performanceThroughputWeight: "Lieferdurchsatz (25%)",
  performanceThroughputDesc:
    "Gemessen durch zusammengeführte PRs und Commit-Häufigkeit. Spiegelt Liefergeschwindigkeit und Konsistenz wider.",
  performanceImpactWeight: "Code-Auswirkung (20%)",
  performanceImpactDesc:
    "Basierend auf geänderten Codezeilen (aus Commits und PRs). Misst das Ausmaß des Beitrags.",
  performanceProblemSolvingWeight: "Problemlösung (15%)",
  performanceProblemSolvingDesc:
    "Basierend auf gelösten Problemen. Misst proaktive Problemlösung und Fehlerbehebung.",
  performanceVersatilityWeight: "Arbeitsvielfalt (10%)",
  performanceVersatilityDesc:
    "Basierend auf Commit-Typ-Verteilung (Features, Bugfixes, Refactoring usw.). Misst die Breite der Beitragstypen.",
  performanceScoreNote:
    "Hinweis: Komponenten, die 100 erreichen, bedeuten maximale Leistung für diese Dimension im angegebenen Zeitraum. Nullwerte zeigen keine Aktivität in dieser Dimension an. Die Gesamtpunktzahl ist der gewichtete Durchschnitt aller Komponenten.",
  performanceDailyValuesTitle: "Tägliche Leistungswerte",
  performanceDailyValuesDescription:
    "Tägliche Aufschlüsselung der Leistungsbewertungen, die zeigt, wie sich die Bewertung im Laufe der Zeit entwickelt hat",
  performanceReasoningTooltip:
    "Diese Bewertung wird basierend auf den Beitragsmetriken des Tages berechnet, einschließlich Commits, PRs, Codeänderungen und gelösten Problemen",
  tableColumnReasoning: "Begründung",

  // Normalized view tooltip
  normalizedViewTooltip:
    "Umschalten zwischen absoluten Werten und normalisierten Werten pro Mitwirkenden. Die normalisierte Ansicht zeigt den durchschnittlichen Beitrag pro Teammitglied oder Repository-Mitwirkenden.",
  normalizedViewLabel: "Ansichtsmodus",
}
